import { useQuery } from '@tanstack/react-query';

interface VehicleDataItem {
  id: number;
  name: string;
  popular?: boolean;
}

interface VehicleDataResponse {
  data: VehicleDataItem[];
}

const fetchVehicleData = async (
  endpoint: 'years' | 'makes' | 'models',
  params?: { year?: string; make?: string; search?: string }
): Promise<VehicleDataItem[]> => {
  const searchParams = new URLSearchParams({ endpoint });
  
  if (params?.year) searchParams.append('year', params.year);
  if (params?.make) searchParams.append('make', params.make);
  if (params?.search) searchParams.append('search', params.search);

  // Construct URL with query params for the edge function
  const baseUrl = 'https://ogripwmsopctiljhqivm.supabase.co/functions/v1/get-vehicle-info';
  const url = `${baseUrl}?${searchParams.toString()}`;
  
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch ${endpoint}: ${response.statusText}`);
  }

  const result: VehicleDataResponse = await response.json();
  return result.data || [];
};

export const useVehicleYears = () => {
  return useQuery({
    queryKey: ['vehicle-years'],
    queryFn: () => fetchVehicleData('years'),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - years don't change often
  });
};

export const useVehicleMakes = (year?: string, search?: string) => {
  return useQuery({
    queryKey: ['vehicle-makes', year, search],
    queryFn: () => fetchVehicleData('makes', { year, search }),
    enabled: !!year,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const useVehicleModels = (year?: string, make?: string, search?: string) => {
  return useQuery({
    queryKey: ['vehicle-models', year, make, search],
    queryFn: () => fetchVehicleData('models', { year, make, search }),
    enabled: !!year && !!make,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};
